import React, { useCallback, useState, useEffect } from "react";
import { useApolloClient } from "@apollo/client";
import {
  Card,
  ErrorMessage,
  Flex,
  QueryLoader,
  SlideoutDrawerV2,
  Tab,
  Tabs,
  Text,
  toast,
  Button,
  ModalV2,
  HelpIndicator,
  Textarea,
} from "@jsq/ds/core";
import { Colors } from "@jsq/ds/styles";
import { addError } from "@jsq/observability";
import { formatError, useReportError } from "#common/hooks/useReportError";
import { PayeeType, PayeeStatus } from "#graphql/supergraphEnums";
import {
  useGetPayeeQuery,
  useApprovePayeeMutation,
  useReturnPayeeMutation,
  useGetPayeeLogsLazyQuery,
} from "#graphql/supergraphHooks";
import { PayeeStatus as PayeeStatusType } from "#graphql/supergraphTypes";
import { clientContext } from "#utils/graphql_utils";
import { LogsList } from "../../../shared/LogsList";
import { PayeeWorkflowModes, PAYEE_TYPE_MAP } from "../../Context/constants";
import { PayeeData } from "../../Context/PayeeContext";
import { PayeeEntityWorkflow } from "../../PayeeEntityWorkflow";
import { PayeeInvestmentWorkflow } from "../../PayeeInvestmentWorkflow";
import { PayeeVendorWorkflow } from "../../PayeeVendorWorkflow";
import { DocumentTracker } from "../DocumentTracker";
import { PayeePaymentPrefDisplayCard } from "../PayeePaymentPrefDisplayCard";
import { ClientDetailsCard } from "./ClientDetailsCard";
import { PayeeDetailsCard } from "./PayeeDetailsCard";
import { payeeLogsConfig } from "./payeeLogsConfig";
import {
  slideoutBody,
  slideoutContainer,
  leftColumn,
  rightColumn,
  rightColumnContent,
  rightColumnHeader,
  rightColumnTabsContainer,
  actionButtonsContainer,
  returnReasonModalContent,
  complexHeaderOverride,
  returnReasonModalOverride,
  cardTitle,
  loaderWrapper,
} from "./styles_vanilla.css";
import { PayeeDetailsProps, PayeeDetailsTabs } from "./types";
import { transformPayeeDataForEdit } from "./utils";

export const PayeeDetails: React.FC<PayeeDetailsProps> = ({ onClose, payeeId }) => {
  const apolloClient = useApolloClient();
  const [activeTab, setActiveTab] = useState<PayeeDetailsTabs>(PayeeDetailsTabs.Overview);
  const [showReturnModal, setShowReturnModal] = useState(false);
  const [returnReason, setReturnReason] = useState("");

  // Reset state when payeeId changes to prevent stale data and tab state
  useEffect(() => {
    if (payeeId) {
      setActiveTab(PayeeDetailsTabs.Overview);
      setShowReturnModal(false);
      setReturnReason("");
      setIsEditing(false); // Reset editing state when switching payees
      setPayeeDataForEdit(undefined); // Clear stale payee data immediately

      // Clear all cached logs data to prevent stale data from previous payees
      apolloClient.cache.evict({
        fieldName: "payeeLogs",
      });
      apolloClient.cache.gc();
    }
  }, [payeeId, apolloClient]);

  const payeeQuery = useGetPayeeQuery({
    variables: {
      payeeId: payeeId!,
    },
    skip: !payeeId,
    context: clientContext(),
  });
  const { data, error } = payeeQuery;

  useReportError(error ? formatError(error) : null);

  const renderError = useCallback(
    () => (
      <div className={loaderWrapper}>
        <ErrorMessage>Content failed to load</ErrorMessage>
      </div>
    ),
    [],
  );

  const [fetchLogs, { data: logsData, loading: logsLoading, error: logsError }] =
    useGetPayeeLogsLazyQuery({
      fetchPolicy: "cache-and-network",
      context: clientContext(),
    });

  useReportError(logsError ? formatError(logsError) : null);

  const handleTabChange = useCallback(
    (tab: PayeeDetailsTabs) => {
      setActiveTab(tab);
      // Fetch logs when Log tab is selected
      if (tab === PayeeDetailsTabs.Log && payeeId) {
        void fetchLogs({ variables: { payeeId } });
      }
    },
    [payeeId, fetchLogs, setActiveTab],
  );

  const payee = data?.payee;
  const [payeeDataForEdit, setPayeeDataForEdit] = useState<PayeeData | undefined>(undefined);

  // Helper function to display colored status
  const renderPayeeStatus = (status: PayeeStatusType) => {
    switch (status) {
      case PayeeStatus.Pending:
        return <Text style={{ color: Colors.Neutral800 }}>Pending review</Text>;
      case PayeeStatus.Approved:
        return <Text style={{ color: Colors.Success600 }}>Approved</Text>;
      case PayeeStatus.Rejected:
        return <Text style={{ color: Colors.Danger600 }}>Returned</Text>;
      default:
        return <Text style={{ color: Colors.Neutral800 }}>{status || "Unknown"}</Text>;
    }
  };

  useEffect(() => {
    let isCancelled = false;

    if (payee) {
      void (async () => {
        const transformedData = await transformPayeeDataForEdit(payee);
        // Only update state if this effect hasn't been cancelled
        if (!isCancelled) {
          setPayeeDataForEdit(transformedData);
        }
      })();
    }

    // Cleanup function to prevent setting state after unmount or payee change
    return () => {
      isCancelled = true;
    };
  }, [payee]);
  const currentUser = data?.currentUser;
  const canRevealPaymentPref = Boolean(currentUser?.canRevealPaymentPrefPermission);

  const [approvePayee, { loading: approveLoading }] = useApprovePayeeMutation({
    context: clientContext(),
    refetchQueries: ["GetPayees"],
    onError: (err) => {
      addError(formatError(err));
      toast.error(err.message, { position: "bottom-right" });
    },
    onCompleted: (result) => {
      if (result) {
        window.location.href = "/canopy/treasury/payees";
      }
    },
  });

  const [returnPayee, { loading: returnLoading }] = useReturnPayeeMutation({
    context: clientContext(),
    refetchQueries: ["GetPayees", "GetPayeeLogs"],
    onError: (err) => {
      addError(formatError(err));
      toast.error(err.message, { position: "bottom-right" });
    },
    onCompleted: (result) => {
      if (result) {
        window.location.href = "/canopy/treasury/payees";
      }
    },
  });

  const handleApprove = useCallback(() => {
    if (!payee?.id) return;
    void approvePayee({
      variables: { payeeId: payee.id },
    });
  }, [approvePayee, payee?.id]);

  const handleReturn = useCallback(() => {
    if (!payee?.id) return;
    setShowReturnModal(true);
  }, [payee?.id]);

  const handleReturnModalClose = useCallback(() => {
    setShowReturnModal(false);
    setReturnReason("");
  }, []);

  const handleReturnSubmit = useCallback(() => {
    if (!payee?.id || !returnReason.trim()) return;

    void returnPayee({
      variables: { payeeId: payee.id, reason: returnReason.trim() },
    });

    handleReturnModalClose();
  }, [returnPayee, payee?.id, returnReason, handleReturnModalClose]);

  const isUserSameAsLastModified = Boolean(
    currentUser?.id && payee?.lastModifiedBy?.id === currentUser.id,
  );

  const showActionButtons = payee?.status === PayeeStatus.Pending;
  const shouldDisableButtons = isUserSameAsLastModified;
  const showEditButton =
    payee?.status === PayeeStatus.Approved || payee?.status === PayeeStatus.Rejected;

  const paymentPrefDocs = payee?.paymentPrefDocuments || [];
  const primaryDocument = paymentPrefDocs[0];
  const documentUrl = primaryDocument?.viewUrl || "";

  const payeeDocs = payee?.payeeDocuments || [];
  const primaryPayeeDocument = payeeDocs[0];

  const getTaxFormLabel = () => {
    if (!primaryPayeeDocument) return "";

    switch (primaryPayeeDocument.documentType) {
      case "us_tax":
        return "W9 (US vendor)";
      case "non_us_tax":
        return "W8 (Non-US vendor)";
      default:
        return "Tax document";
    }
  };

  const taxFormLabel = getTaxFormLabel();

  const hasPayeeDetailsToShow = Boolean(
    payee?.name ||
      (payee?.address &&
        [
          payee.address.street,
          payee.address.street2,
          payee.address.street3,
          payee.address.city,
          payee.address.state?.name || payee.address.region,
          payee.address.postalCode,
          payee.address.country?.name,
        ].filter(Boolean).length > 0) ||
      payee?.taxId ||
      taxFormLabel ||
      (primaryPayeeDocument && primaryPayeeDocument.viewUrl) ||
      (payee?.form1099Required !== null && payee?.form1099Required !== undefined),
  );

  const [isEditing, setIsEditing] = useState<boolean>(false);

  const handleEditPayee = useCallback(() => {
    setIsEditing(true);
  }, []);

  const handleExit = useCallback(() => {
    setIsEditing(false);
  }, []);

  const renderEditWorkflow = useCallback(() => {
    if (!isEditing || !payeeDataForEdit) return null;
    switch (payee?.payeeType) {
      case PayeeType.Vendor:
        return (
          <PayeeVendorWorkflow
            onExit={handleExit}
            payeeState={payeeDataForEdit}
            workflowState={{ stepNumber: 0, mode: PayeeWorkflowModes.EDIT }}
          />
        );
      case PayeeType.Entity:
        return (
          <PayeeEntityWorkflow
            onExit={handleExit}
            payeeState={payeeDataForEdit}
            workflowState={{ stepNumber: 0, mode: PayeeWorkflowModes.EDIT }}
          />
        );
      case PayeeType.Investment:
        return (
          <PayeeInvestmentWorkflow
            onExit={handleExit}
            payeeState={payeeDataForEdit}
            workflowState={{ stepNumber: 0, mode: PayeeWorkflowModes.EDIT }}
          />
        );
      default:
        return null;
    }
  }, [isEditing, handleExit, payeeDataForEdit, payee?.payeeType]);

  return (
    <>
      <SlideoutDrawerV2
        // @ts-expect-error - OverlayStyle enum properly exported but auto-formatter removes import
        overlayStyle="transparent"
        isOpen={!!payeeId && !isEditing}
        onClose={onClose}
        data-testid="payee-details-slideout"
      >
        <div className={slideoutBody}>
          <QueryLoader result={payeeQuery} renderError={renderError}>
            {!payeeId ? null : !payee || payee.id !== payeeId ? (
              renderError()
            ) : (
              <Flex className={slideoutContainer}>
                <Flex className={leftColumn}>
                  {!primaryDocument ? (
                    <Flex flexDirection="column" alignItems="center" justifyContent="center">
                      <Text variant="regular-md" color="muted">
                        No payment preference documents available for this payee.
                      </Text>
                    </Flex>
                  ) : (
                    <DocumentTracker
                      documentName={primaryDocument.name}
                      documentUrl={documentUrl}
                      slideoutMode={true}
                    />
                  )}
                </Flex>
                <Flex className={rightColumn}>
                  <Flex
                    flexDirection="column"
                    className={`${rightColumnHeader} ${complexHeaderOverride}`}
                  >
                    <SlideoutDrawerV2.ComplexHeader
                      title={payee.name}
                      subtitles={[
                        payee.payeeType === "vendor"
                          ? "Vendor"
                          : payee.payeeType === "entity"
                            ? "Entity"
                            : payee.payeeType === "investment"
                              ? "Investment"
                              : "Payee",
                        renderPayeeStatus(payee.status),
                      ]}
                    />
                    <Flex className={rightColumnTabsContainer}>
                      <Tabs>
                        <Tab
                          role="tab"
                          text={PayeeDetailsTabs.Overview}
                          onClick={() => handleTabChange(PayeeDetailsTabs.Overview)}
                          active={activeTab === PayeeDetailsTabs.Overview}
                        />
                        <Tab
                          role="tab"
                          text={PayeeDetailsTabs.Log}
                          onClick={() => handleTabChange(PayeeDetailsTabs.Log)}
                          active={activeTab === PayeeDetailsTabs.Log}
                        />
                      </Tabs>
                    </Flex>
                  </Flex>
                  <Flex flexDirection="column" className={rightColumnContent} gap={16}>
                    {activeTab === PayeeDetailsTabs.Overview && (
                      <>
                        {payee.arena?.name && (
                          <ClientDetailsCard clientName={payee.arena.name} />
                        )}

                        {hasPayeeDetailsToShow && payee.payeeType === PayeeType.Vendor && (
                          <PayeeDetailsCard
                            payeeName={payee.name}
                            payeeType={PAYEE_TYPE_MAP[payee.payeeType]}
                            address={payee.address}
                            taxId={payee.taxId}
                            taxFormLabel={taxFormLabel}
                            primaryPayeeDocument={primaryPayeeDocument}
                            form1099Required={payee.form1099Required}
                          />
                        )}

                        {payee.paymentPref?.paymentPrefFull && (
                          <Card padding="lg">
                            <Text className={cardTitle} variant="secondary-md">
                              PAYMENT INSTRUCTIONS
                            </Text>
                            <PayeePaymentPrefDisplayCard
                              paymentPref={payee.paymentPref.paymentPrefFull}
                              displayRevealDetails={canRevealPaymentPref}
                            />
                          </Card>
                        )}
                      </>
                    )}
                    {activeTab === PayeeDetailsTabs.Log && (
                      <Flex flexDirection="column" gap={16}>
                        <LogsList
                          logs={logsData?.payee?.logs || []}
                          loading={logsLoading}
                          error={logsError}
                          config={payeeLogsConfig}
                        />
                      </Flex>
                    )}
                  </Flex>
                  {showActionButtons ? (
                    <Flex
                      className={actionButtonsContainer}
                      justifyContent="flex-end"
                      alignItems="center"
                      gap={12}
                    >
                      {shouldDisableButtons ? (
                        <HelpIndicator
                          id="return-help"
                          content="You cannot review your own change."
                          placement="top"
                        >
                          <div>
                            <Button
                              variant="neutral"
                              onClick={handleReturn}
                              disabled={shouldDisableButtons || returnLoading}
                              isLoading={returnLoading}
                            >
                              {`Return ${payee.payeeType.toLowerCase()}`}
                            </Button>
                          </div>
                        </HelpIndicator>
                      ) : (
                        <Button
                          variant="neutral"
                          onClick={handleReturn}
                          disabled={shouldDisableButtons || returnLoading}
                          isLoading={returnLoading}
                        >
                          {`Return ${payee.payeeType.toLowerCase()}`}
                        </Button>
                      )}
                      {shouldDisableButtons ? (
                        <HelpIndicator
                          id="approve-help"
                          content="You cannot review your own change."
                          placement="top"
                        >
                          <div>
                            <Button
                              variant="primary"
                              onClick={handleApprove}
                              disabled={shouldDisableButtons || approveLoading}
                              isLoading={approveLoading}
                            >
                              {`Approve ${payee.payeeType.toLowerCase()}`}
                            </Button>
                          </div>
                        </HelpIndicator>
                      ) : (
                        <Button
                          variant="primary"
                          onClick={handleApprove}
                          disabled={shouldDisableButtons || approveLoading}
                          isLoading={approveLoading}
                        >
                          {`Approve ${payee.payeeType.toLowerCase()}`}
                        </Button>
                      )}
                    </Flex>
                  ) : (
                    showEditButton && (
                      <Flex
                        className={actionButtonsContainer}
                        justifyContent="flex-end"
                        alignItems="center"
                        gap={12}
                      >
                        <Button variant="neutral" onClick={handleEditPayee}>
                          Edit
                        </Button>

                        <Button variant="primary" onClick={handleEditPayee} disabled={true}>
                          Archive
                        </Button>
                      </Flex>
                    )
                  )}
                </Flex>
              </Flex>
            )}
          </QueryLoader>
        </div>
      </SlideoutDrawerV2>

      {/* Return Reason Modal */}
      <ModalV2
        show={showReturnModal}
        onHide={handleReturnModalClose}
        title="Return reason"
        aria-describedby="return-reason-description"
        contentClassName={returnReasonModalOverride}
      >
        <Flex flexDirection="column" gap={20} className={returnReasonModalContent}>
          <div id="return-reason-description">
            <Textarea
              size="small"
              value={returnReason}
              onChange={(e) => setReturnReason(e.target.value)}
              placeholder={`Please add the reason for returning the ${payee?.payeeType.toLowerCase()}`}
              isFullWidth
            />
          </div>
          <Flex flexDirection="row" justifyContent="flex-end" gap={12}>
            <Button variant="neutral" onClick={handleReturnModalClose}>
              Cancel
            </Button>
            <Button
              variant="primary"
              onClick={handleReturnSubmit}
              disabled={!returnReason.trim() || returnLoading}
              isLoading={returnLoading}
            >
              Submit reason
            </Button>
          </Flex>
        </Flex>
      </ModalV2>
      {renderEditWorkflow()}
    </>
  );
};
//comment
export default PayeeDetails;
